/** 错误码 → 中文可读文案，以及统一的错误提示入口 */

import { message as staticMessage, Modal as StaticModal } from 'antd';
import type { MessageInstance } from 'antd/es/message/interface';
import type { HookAPI as ModalHookAPI } from 'antd/es/modal/useModal';
import type { ApiRequestError } from '@/api/client';
import { isApiRequestError } from '@/api/client';

/**
 * AntD 的静态方法（Modal.error / message.error）**拿不到 ConfigProvider 的主题**：
 * 它们在 React 树之外自建渲染根，用的是 AntD 默认主题。
 * 后果是深色模式下弹出一个白底对话框——而且只在弹窗真的打开时才看得见，
 * 静态截图和构建检查都发现不了。
 *
 * 所以由 App 层把 `App.useApp()` 的实例注册进来，这里优先用它。
 * 与 client.ts 的 setUnauthorizedHandler 是同一个套路：
 * 工具模块不该依赖 React，但可以接受 React 层注入能力。
 */
let appApi: { message: MessageInstance; modal: ModalHookAPI } | null = null;

export function setAppApi(api: { message: MessageInstance; modal: ModalHookAPI } | null): void {
  appApi = api;
}

/** 注册之前（极早期错误）退回静态方法：主题不对，但总比什么都不提示强 */
const msg = (): MessageInstance | typeof staticMessage => appApi?.message ?? staticMessage;
const modal = (): ModalHookAPI | typeof StaticModal => appApi?.modal ?? StaticModal;

/** docs/04 §8 的 HTTP 层错误码 */
const HTTP_ERROR_TEXT: Record<string, string> = {
  INVALID_REQUEST: '请求格式有误',
  UNAUTHORIZED: '登录已失效，请重新登录',
  CONFLICT: '与当前状态冲突，请刷新后重试',
  NOT_FOUND: '资源不存在',
  FORBIDDEN: '无权访问该资源',
  WORKFLOW_NOT_FOUND: '工作流不存在',
  RUN_NOT_FOUND: '运行记录不存在',
  GRAPH_UNCHANGED: '与上一版内容完全相同，无需发布',
  RUN_ALREADY_FINISHED: '该运行已结束，无法取消',
  CREDENTIAL_IN_USE: '凭据正被已发布的工作流引用，无法删除',
  WORKFLOW_HAS_RUNNING: '仍有运行中的实例，无法删除',
  INPUT_VALIDATION_FAILED: '输入参数不符合工作流的入参定义',
  GRAPH_VALIDATION_FAILED: '图校验未通过',
  WORKFLOW_NOT_PUBLISHED: '工作流尚未发布，无法触发',
  RATE_LIMITED: '请求过于频繁，请稍后重试',
  INTERNAL_ERROR: '服务端内部错误',
  NO_AVAILABLE_WORKER: '当前无存活 Worker，任务无法执行',
  AI_COMPOSE_FAILED: 'AI 未能生成可用的工作流，请调整需求后重试',
  NETWORK_ERROR: '网络不可达，请检查后端服务是否启动',
};

/** docs/04 §8 的 Run 级错误码，出现在 run.error / node_run.error 里 */
export const RUN_ERROR_TEXT: Record<string, string> = {
  NODE_EXECUTION_FAILED: '节点重试耗尽仍失败',
  NODE_TIMEOUT: '节点执行超时',
  RUN_TIMEOUT: '整体执行超时',
  VAR_NOT_FOUND: '变量引用解析失败',
  CONDITION_TYPE_ERROR: '条件比较的两侧类型不可比',
  LLM_JSON_PARSE_ERROR: '结构化输出解析失败',
  CONTEXT_OVERFLOW: 'prompt 超出模型上下文长度',
  TOKEN_BUDGET_EXCEEDED: '超出该次运行的 Token 预算',
  CODE_SANDBOX_VIOLATION: 'code 节点触碰沙箱限制',
  SSRF_BLOCKED: 'http 节点目标命中私网或协议黑名单',
  WORKER_LOST: 'Worker 失联且重试耗尽',
  SUBWORKFLOW_DEPTH_EXCEEDED: '子工作流嵌套层级过深',
};

export function errorCodeText(code: string): string {
  return HTTP_ERROR_TEXT[code] ?? RUN_ERROR_TEXT[code] ?? code;
}

export function errorMessage(e: unknown): string {
  if (isApiRequestError(e)) {
    // 服务端 message 已是面向人的中文，优先用它；码表只作兜底
    return e.message || errorCodeText(e.code);
  }
  if (e instanceof Error) return e.message;
  return String(e);
}

/** 轻提示。ApiRequestError 会带上 request_id，便于对着日志排查。 */
export function showError(e: unknown, prefix?: string): void {
  if (e instanceof DOMException && e.name === 'AbortError') return;
  const text = [prefix, errorMessage(e)].filter(Boolean).join('：');
  if (isApiRequestError(e) && e.requestId) {
    void msg().error({ content: `${text}（request_id: ${e.requestId}）`, duration: 5 });
  } else {
    void msg().error(text);
  }
}

/** 校验错误这类需要逐条看的，用 Modal 而非一闪而过的 toast */
export function showErrorDetail(e: unknown, title = '操作失败'): void {
  const err: ApiRequestError | null = isApiRequestError(e) ? e : null;
  modal().error({
    title,
    content: [errorMessage(e), err?.requestId ? `request_id: ${err.requestId}` : '']
      .filter(Boolean)
      .join('\n'),
    width: 520,
  });
}
